/**
 * O FUNIL DEPOIS DA DECISÃO (D13, pedido do Pedro em 2026-10-05).
 *
 * "Ele só está construído até a parte de avaliação. Após tomar a decisão sobre
 * uma oportunidade, deve chegar a fase de disputa."
 *
 *   aprovada      -> o botão que inicia a preparação da documentação
 *   preparação    -> a lista do que o edital exige, com os arquivos, e no FIM
 *                    dela o botão "Entrou em disputa"
 *   em disputa    -> os dois botões de resultado: Vencida e Perdida
 *   resultado     -> o que foi informado, por quem e quando
 *
 * ── três regras que o desenho respeita ───────────────────────────────────
 *
 * **O botão da disputa funciona sempre, e avisa.** Com item pendente ele não
 * fica apagado: mostra quantos faltam e pede a confirmação aqui mesmo, em dois
 * passos, sem caixa do navegador. O servidor grava o número.
 *
 * **Perdida pelo prazo não é resultado informado.** A aprovada cujo prazo
 * passou sem a disputa ser informada aparece em Perdidas, mas ninguém disse
 * que perdeu — então a tela diz o motivo e deixa informar a disputa.
 *
 * **Todo passo pode ser desfeito**, um de cada vez, e fica no histórico.
 *
 * Toda escrita devolve o estado inteiro, que vai direto para o cache: a lista
 * não pisca nem espera uma segunda busca para saber quantos itens faltam.
 */
import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ErroApi,
  type Disputa as Estado,
  type ItemDaDisputa,
  type LicitacaoDetalhe,
} from "../api/cliente";
import { useSessao } from "../sessao";
import { dataHora, dia } from "../formato";
import { Secao } from "./Secao";
import css from "./Disputa.module.css";

const ORIGEM: Record<ItemDaDisputa["origem"], string> = {
  edital: "do edital",
  atestado: "atestado",
  manual: "acrescentado",
};

const ESTADOS: [ItemDaDisputa["estado"], string][] = [
  ["pendente", "pendente"],
  ["pronto", "pronto"],
  ["nao_se_aplica", "não se aplica"],
];

const EVENTO: Record<string, string> = {
  preparacao: "Preparação iniciada",
  em_disputa: "Entrou em disputa",
  vencida: "Resultado: vencida",
  perdida: "Resultado: perdida",
  desfeito: "Desfeito",
};

function tamanho(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace(".", ",")} MB`;
}

export function Disputa({ d }: { d: LicitacaoDetalhe }) {
  const { podeOperar } = useSessao();
  const cliente = useQueryClient();
  const chave = ["disputa", d.id];
  const consulta = useQuery({ queryKey: chave, queryFn: () => api.disputa(d.id) });
  const [novo, setNovo] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [observacao, setObservacao] = useState("");

  // A etapa muda a aba, o cartão e as contagens: tudo o que a mostra.
  const guarda = (estado: Estado) => {
    cliente.setQueryData(chave, estado);
    void cliente.invalidateQueries({ queryKey: ["licitacao", d.id] });
    void cliente.invalidateQueries({ queryKey: ["licitacoes"] });
    void cliente.invalidateQueries({ queryKey: ["facetas"] });
    void cliente.invalidateQueries({ queryKey: ["acervo"] });
  };
  const acao = useMutation({
    mutationFn: (f: () => Promise<Estado>) => f(),
    onSuccess: (estado) => {
      setConfirmando(false);
      guarda(estado);
    },
  });
  const faz = (f: () => Promise<Estado>) => acao.mutate(f);

  // Só a aprovada tem fim de funil; a rota devolve `etapa` nula para as outras.
  const e = consulta.data;
  if (d.decisao !== "aprovada" || !e || !e.etapa) return null;

  const ocupado = acao.isPending;
  const erro = acao.error;
  const podeMexer = podeOperar && e.aberta;
  const temResultado = e.resultado != null;
  const haPasso = e.preparacao_em != null || e.em_disputa_em != null || temResultado;
  const oQueDesfaz = temResultado
    ? "o resultado"
    : e.em_disputa_em
      ? "a entrada na disputa"
      : "o início da preparação";

  const entrar = () => {
    if (e.pendentes > 0 && !confirmando) {
      setConfirmando(true);
      return;
    }
    faz(() => api.entrarEmDisputa(d.id, e.pendentes > 0));
  };

  const lista = e.preparacao_em != null && (
    <ListaDaDocumentacao
      id={d.id}
      e={e}
      podeMexer={podeMexer}
      ocupado={ocupado}
      faz={faz}
      novo={novo}
      setNovo={setNovo}
    />
  );

  return (
    <Secao titulo="Disputa">
      <div className={css.tudo}>
        {/* ── aprovada: o botão que abre a preparação ── */}
        {e.etapa === "aprovada" && (
          <>
            <p className={css.texto}>
              Aprovada. Ao começar a montar a participação, inicie a preparação: o
              sistema monta a lista do que o edital exige e dos atestados a
              apresentar, e cada item guarda os arquivos.
            </p>
            {podeOperar && (
              <button
                type="button"
                className={css.principal}
                onClick={() => faz(() => api.iniciarPreparacao(d.id))}
                disabled={ocupado}
              >
                Preparar documentação
              </button>
            )}
          </>
        )}

        {/* ── perdida PELO PRAZO: ninguém informou resultado ── */}
        {e.prazo_encerrado && (
          <div className={`${css.faixa} ${css.perdida}`}>
            <b>Perdida pelo prazo</b>
            <span>
              O prazo de proposta passou sem a disputa ser informada, e por isso a
              licitação está em Perdidas. Se a Nectar disputou, informe a disputa.
            </span>
          </div>
        )}

        {/* ── em disputa: os dois botões do resultado ── */}
        {e.etapa === "em_disputa" && (
          <>
            <div className={`${css.faixa} ${css.emDisputa}`}>
              <b>Em disputa</b>
              <span>
                desde {dia(e.em_disputa_em)} · informado por {e.em_disputa_por}
                {e.pendentes_na_entrada
                  ? ` · entrou com ${e.pendentes_na_entrada} item(ns) pendente(s) na documentação`
                  : ""}
              </span>
            </div>
            {podeOperar && (
              <div className={css.resultado}>
                <label className={css.rotulo} htmlFor={`obs-${d.id}`}>
                  Observação sobre o resultado (opcional)
                </label>
                <textarea
                  id={`obs-${d.id}`}
                  className={css.campo}
                  rows={2}
                  value={observacao}
                  onChange={(ev) => setObservacao(ev.currentTarget.value)}
                  placeholder="Ex.: contrato de 12 meses; ou: perdemos no preço para a segunda colocada."
                  disabled={ocupado}
                />
                <div className={css.botoes}>
                  <button
                    type="button"
                    className={css.vencida}
                    disabled={ocupado}
                    onClick={() =>
                      faz(() =>
                        api.informarResultado(d.id, {
                          resultado: "vencida",
                          observacao: observacao.trim() || null,
                        }),
                      )
                    }
                  >
                    Vencida
                  </button>
                  <button
                    type="button"
                    className={css.perdeu}
                    disabled={ocupado}
                    onClick={() =>
                      faz(() =>
                        api.informarResultado(d.id, {
                          resultado: "perdida",
                          observacao: observacao.trim() || null,
                        }),
                      )
                    }
                  >
                    Perdida
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* ── o resultado informado ── */}
        {temResultado && (
          <div className={`${css.faixa} ${e.resultado === "vencida" ? css.ganha : css.perdida}`}>
            <b>{e.resultado === "vencida" ? "Vencida" : "Perdida"}</b>
            <span>
              informado por {e.resultado_por} em {dia(e.resultado_em)}
              {e.resultado_obs ? ` — ${e.resultado_obs}` : ""}
            </span>
          </div>
        )}

        {/* ── a documentação: aberta na preparação, recolhida depois ── */}
        {lista &&
          (e.etapa === "preparacao" || e.prazo_encerrado ? (
            lista
          ) : (
            <details className={css.recolhida}>
              <summary>
                Documentação ({e.itens.length} {e.itens.length === 1 ? "item" : "itens"}
                {e.pendentes ? `, ${e.pendentes} pendente(s)` : ""})
              </summary>
              {lista}
            </details>
          ))}

        {/* ── o botão do FIM da documentação ── */}
        {podeOperar && (e.etapa === "preparacao" || e.prazo_encerrado) && (
          <div className={css.fim}>
            {confirmando ? (
              <div className={css.confirma} role="alertdialog" aria-label="Confirmar a entrada na disputa">
                <p>
                  <b>{e.pendentes} item(ns) pendente(s)</b> na documentação. Informar que
                  a licitação entrou em disputa assim mesmo?
                </p>
                <div className={css.botoes}>
                  <button type="button" className={css.principal} onClick={entrar} disabled={ocupado}>
                    Entrou em disputa assim mesmo
                  </button>
                  <button
                    type="button"
                    className={css.secundario}
                    onClick={() => setConfirmando(false)}
                    disabled={ocupado}
                  >
                    Voltar à lista
                  </button>
                </div>
              </div>
            ) : (
              <>
                <button type="button" className={css.principal} onClick={entrar} disabled={ocupado}>
                  {e.prazo_encerrado ? "A Nectar entrou em disputa" : "Entrou em disputa"}
                </button>
                {e.pendentes > 0 && (
                  <p className={css.ajuda}>
                    {e.pendentes} item(ns) pendente(s). O botão funciona assim mesmo, e
                    pede a confirmação.
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {erro && (
          <p className={css.erro}>
            {erro instanceof ErroApi || erro instanceof Error
              ? erro.message
              : "não deu para gravar"}
          </p>
        )}

        {podeOperar && haPasso && (
          <button
            type="button"
            className={css.desfazer}
            onClick={() => faz(() => api.desfazerNaDisputa(d.id))}
            disabled={ocupado}
            title="Volta um passo. O que foi preparado continua guardado."
          >
            Desfazer {oQueDesfaz}
          </button>
        )}

        {e.historico.length > 0 && (
          <details className={css.recolhida}>
            <summary>Histórico da disputa ({e.historico.length})</summary>
            <ul className={css.historico}>
              {e.historico.map((h, i) => (
                <li key={i}>
                  <b>{EVENTO[h.evento] ?? h.evento}</b>
                  {h.detalhe ? ` — ${h.detalhe}` : ""}
                  <span>
                    {h.por} · {dataHora(h.em)}
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Secao>
  );
}

/** A lista da documentação: os itens, o estado de cada um e os arquivos. */
function ListaDaDocumentacao({
  id,
  e,
  podeMexer,
  ocupado,
  faz,
  novo,
  setNovo,
}: {
  id: string;
  e: Estado;
  podeMexer: boolean;
  ocupado: boolean;
  faz: (f: () => Promise<Estado>) => void;
  novo: string;
  setNovo: (v: string) => void;
}) {
  return (
    <div className={css.documentacao}>
      <p className={css.resumo}>
        <b>Documentação</b>
        <span>
          {e.itens.length === 0
            ? "nenhum item ainda"
            : `${e.itens.length - e.pendentes} de ${e.itens.length} resolvido(s)`}
          {e.preparacao_por ? ` · iniciada por ${e.preparacao_por} em ${dia(e.preparacao_em)}` : ""}
        </span>
      </p>
      {!e.tem_pre_analise && (
        <p className={css.ajuda}>
          Ainda não há pré-análise desta licitação, e por isso a lista não trouxe o
          que o edital exige. Acrescente os itens à mão; quando a pré-análise chegar,
          use "Buscar na pré-análise".
        </p>
      )}

      <ul className={css.itens}>
        {e.itens.map((i) => (
          <Item key={i.id} id={id} i={i} podeMexer={podeMexer} ocupado={ocupado} faz={faz}
                maxMb={e.arquivo_max_mb} extensoes={e.extensoes} />
        ))}
      </ul>

      {podeMexer && (
        <form
          className={css.novo}
          onSubmit={(ev) => {
            ev.preventDefault();
            const titulo = novo.trim();
            if (!titulo) return;
            faz(async () => {
              const estado = await api.novoItemDaDisputa(id, { titulo });
              setNovo("");
              return estado;
            });
          }}
        >
          <input
            className={css.campo}
            value={novo}
            onChange={(ev) => setNovo(ev.currentTarget.value)}
            placeholder="Acrescentar item: proposta de preços, procuração, declaração…"
            aria-label="Novo item da documentação"
            maxLength={300}
            disabled={ocupado}
          />
          <button type="submit" className={css.secundario} disabled={ocupado || !novo.trim()}>
            Acrescentar
          </button>
          {e.tem_pre_analise && (
            <button
              type="button"
              className={css.secundario}
              onClick={() => faz(() => api.sugerirItensDaDisputa(id))}
              disabled={ocupado}
              title="Acrescenta o que a pré-análise sugere e ainda não está na lista. Não duplica nem traz de volta o que foi retirado."
            >
              Buscar na pré-análise
            </button>
          )}
        </form>
      )}
    </div>
  );
}

function Item({
  id,
  i,
  podeMexer,
  ocupado,
  faz,
  maxMb,
  extensoes,
}: {
  id: string;
  i: ItemDaDisputa;
  podeMexer: boolean;
  ocupado: boolean;
  faz: (f: () => Promise<Estado>) => void;
  maxMb: number;
  extensoes: string[];
}) {
  const [obs, setObs] = useState(i.observacao ?? "");
  const arquivo = useRef<HTMLInputElement>(null);

  return (
    <li className={css.item} data-estado={i.estado}>
      <div className={css.cabeca}>
        <div className={css.titulo}>
          <b>{i.titulo}</b>
          <span className={css.origem}>{ORIGEM[i.origem]}</span>
        </div>
        {podeMexer ? (
          <div className={css.tres} role="group" aria-label={`Estado de ${i.titulo}`}>
            {ESTADOS.map(([valor, rotulo]) => (
              <button
                key={valor}
                type="button"
                data-ativo={i.estado === valor}
                aria-pressed={i.estado === valor}
                disabled={ocupado}
                onClick={() =>
                  i.estado === valor
                    ? undefined
                    : faz(() => api.mudarItemDaDisputa(id, i.id, { estado: valor }))
                }
              >
                {rotulo}
              </button>
            ))}
          </div>
        ) : (
          <span className={css.estadoFixo}>
            {ESTADOS.find(([v]) => v === i.estado)?.[1] ?? i.estado}
          </span>
        )}
      </div>
      {i.exigencia && <p className={css.exigencia}>{i.exigencia}</p>}

      {podeMexer ? (
        <input
          className={css.obs}
          value={obs}
          onChange={(ev) => setObs(ev.currentTarget.value)}
          // Grava ao sair do campo, e só quando mudou: uma tecla por pedido
          // faria a lista inteira voltar do servidor a cada letra.
          onBlur={() => {
            const texto = obs.trim();
            if (texto !== (i.observacao ?? "")) {
              faz(() => api.mudarItemDaDisputa(id, i.id, { observacao: texto || null }));
            }
          }}
          placeholder="Observação: onde está, quem emite, validade…"
          aria-label={`Observação de ${i.titulo}`}
          maxLength={2000}
          disabled={ocupado}
        />
      ) : (
        i.observacao && <p className={css.exigencia}>{i.observacao}</p>
      )}

      {i.arquivos.length > 0 && (
        <ul className={css.arquivos}>
          {i.arquivos.map((a) => (
            <li key={a.id}>
              {/* `download` + rota que responde como anexo: nunca abre no
                  navegador, e o cookie acompanha por ser a mesma origem. */}
              <a href={api.urlDoArquivoDaDisputa(id, a.id)} download={a.nome}>
                {a.nome}
              </a>
              <span>
                {tamanho(a.bytes)} · {a.enviado_por} · {dia(a.enviado_em)}
              </span>
              {podeMexer && (
                <button
                  type="button"
                  className={css.remover}
                  onClick={() => faz(() => api.removerArquivoDaDisputa(id, a.id))}
                  disabled={ocupado}
                  title="Apaga o arquivo do sistema. Fica o registro de quem enviou e de quem removeu."
                >
                  remover
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {podeMexer && (
        <div className={css.pe}>
          <input
            ref={arquivo}
            type="file"
            className={css.escondido}
            accept={extensoes.join(",")}
            onChange={(ev) => {
              const f = ev.currentTarget.files?.[0];
              ev.currentTarget.value = "";
              if (f) faz(() => api.anexarNaDisputa(id, i.id, f));
            }}
            aria-label={`Anexar arquivo a ${i.titulo}`}
          />
          <button
            type="button"
            className={css.anexar}
            onClick={() => arquivo.current?.click()}
            disabled={ocupado}
            title={`Até ${maxMb} MB. Aceitos: ${extensoes.join(", ")}`}
          >
            Anexar arquivo
          </button>
          <button
            type="button"
            className={css.remover}
            onClick={() => faz(() => api.removerItemDaDisputa(id, i.id))}
            disabled={ocupado}
            title="Tira o item da lista, com os arquivos dele."
          >
            retirar item
          </button>
        </div>
      )}
    </li>
  );
}
